import _ from 'lodash'
import { FTWebSocket } from '@/utils/FTWebSocket'
import { fetchApprovalStatusStatistics } from '@/services/dataMaintenance'

const approvalStatusChangeActionTypes = ['APPROVED', 'REJECTED']

/**
 * Mutation types of the store
 */
const mutationTypes = {
  FETCHING: 'FETCHING',
  FETCHED: 'FETCHED',
  SET_APPROVAL_STATISTICS: 'SET_APPROVAL_STATISTICS',
  SET_PROMOTION_UPDATED_EVENT: 'SET_PROMOTION_UPDATED_EVENT'
}

/**
 * Initial state
 */
const state = {
  loading: false,
  fetching: false,
  approvalStatistics: {
    products: {
      requiringAmendmentsCount: 0
    },
    promotions: {
      requiringAmendmentsCount: 0
    },
    priceDrops: {
      requiringAmendmentsCount: 0
    },
    volumePromotions: {
      requiringAmendmentsCount: 0
    }
  },
  promotionUpdatedEvent: null
}

/**
 * Getters
 */
const getters = {
  productsRequiringAmendmentsCount(state) {
    return state.approvalStatistics?.products?.requiringAmendmentsCount || 0
  },

  promotionsRequiringAmendmentsCount(state) {
    return state.approvalStatistics?.promotions?.requiringAmendmentsCount || 0
  },

  priceDropsRequiringAmendmentsCount(state) {
    return state.approvalStatistics?.priceDrops?.requiringAmendmentsCount || 0
  },

  volumePromotionsRequiringAmendmentsCount(state) {
    return (
      state.approvalStatistics?.volumePromotions?.requiringAmendmentsCount || 0
    )
  },

  totalRequiringAmendmentsCount(state) {
    return (
      getters.productsRequiringAmendmentsCount(state) +
      getters.promotionsRequiringAmendmentsCount(state) +
      getters.priceDropsRequiringAmendmentsCount(state) +
      getters.volumePromotionsRequiringAmendmentsCount(state)
    )
  }
}

/**
 * Actions
 */
const actions = {
  /**
   * @desc Fetch total of order queries
   */
  fetchApprovalStatusStatistics({ commit }) {
    commit(mutationTypes.FETCHING)
    return fetchApprovalStatusStatistics().then((response) => {
      commit(mutationTypes.FETCHED)
      commit(mutationTypes.SET_APPROVAL_STATISTICS, response)
    })
  },

  subscribeToProductUpdatedEvents({ commit, dispatch, rootState }) {
    return FTWebSocket.subscribe(
      '/user/queue/products/updated',
      async (event) => {
        const selectedSupplierId = rootState?.preferences?.selectedSupplierId
        // websocket sends events for all suppliers linked to user account
        // we need to only process this event if it relates to selected supplier
        if (event.supplierId === selectedSupplierId) {
          console.log('Received product approved websocket notification', event)
          if (approvalStatusChangeActionTypes.includes(event.actionType)) {
            await dispatch('fetchApprovalStatusStatistics')
            await dispatch(
              'products/refreshProductsForSupplierEvent',
              { supplierId: event.supplierId },
              { root: true }
            )
            const message = `A Product submitted for approval has just been ${event.actionType}`

            dispatch(
              'notifications/addNotification',
              {
                color: 'warning',
                closable: true,
                text: message,
                timeout: 10000
              },
              { root: true }
            )
          }
        }
      }
    )
  },

  subscribeToPromotionUpdatedEvents({ commit, dispatch, rootState }) {
    return FTWebSocket.subscribe(
      '/user/queue/promotions/updated',
      async (event) => {
        const selectedSupplierId = rootState?.preferences?.selectedSupplierId
        // websocket sends events for all suppliers linked to user account
        // we need to only process this event if it relates to selected supplier
        if (event.supplierId === selectedSupplierId) {
          console.log(
            'Received promotion approved websocket notification',
            event
          )
          if (approvalStatusChangeActionTypes.includes(event.actionType)) {
            await dispatch('fetchApprovalStatusStatistics')
            commit(mutationTypes.SET_PROMOTION_UPDATED_EVENT, event)
            const promotionType = _.startCase(
              _.toLower(event.promotionType.replaceAll('_', ' '))
            )
            const message = `A ${promotionType} submitted for approval has just been ${event.actionType}`

            dispatch(
              'notifications/addNotification',
              {
                color: 'warning',
                closable: true,
                text: message,
                timeout: 10000
              },
              { root: true }
            )
          }
        }
      }
    )
  }
}

/**
 * Mutations
 */
const mutations = {
  [mutationTypes.FETCHING](state) {
    state.fetching = true
    state.loading = true
  },

  [mutationTypes.FETCHED](state) {
    state.fetching = false
    state.loading = false
  },

  [mutationTypes.SET_APPROVAL_STATISTICS](state, approvalStatistics) {
    state.approvalStatistics = { ...approvalStatistics }
  },

  [mutationTypes.SET_PROMOTION_UPDATED_EVENT](state, event) {
    state.promotionUpdatedEvent = { ...event }
  }
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state
}
